/* The notebook — pages you draw and type on.

   Strokes are stored as VECTORS in 0-1 page coordinates, not pixels, so a page
   redraws crisply at any size and an eraser can lift a single stroke. Pages live
   in a Firestore subcollection, one document each, because a notebook anyone
   actually uses would sail past the 1 MB per-document cap as a single blob.

   Moved out of app.html unchanged apart from the onReady() wrapper. That
   wrapper is not cosmetic: this used to open with `if (!wrap) return;`, and
   running from the bundle in <head> — before the markup exists — that guard
   would fire on every load and the whole editor would silently not be there.
*/

import { onReady } from '../lib/ready.js';

export function initNotebook() {
  const $ = id => document.getElementById(id);
  const wrap = $('nb-wrap');
  if (!wrap) return;   // now only true if the markup is genuinely absent

  const NB_PALETTE = ['#111827', '#6b7280', '#dc2626', '#ea580c', '#ca8a04', '#16a34a',
                      '#0891b2', '#2563eb', '#4f46e5', '#9333ea', '#db2777', '#ffffff'];

  let nb = null;   // { note, pages:[{id,index,strokes,texts,isCover}], dirty:Set }
  const nbRedo = new Map();   // page index -> strokes lifted by undo, newest last
  let tool = 'pen', toolType = 'pen', color = '#111827', width = 1.6;
  let eraserMode = 'standard', eraserR = 0.012;
  let activePageIdx = 0;
  let saveTimer = null;
  let editingText = null;

  /* Points are stored 0–1 relative to the page, so a stroke drawn on a phone
     lands in the same place on a desktop and stays crisp at any zoom. */
  const rel = (e, canvas) => {
    const r = canvas.getBoundingClientRect();
    /* Third value is pen pressure, 0–1. A mouse reports 0.5 when down and 0
       when it has no idea, so anything falsy becomes 0.5 — otherwise every
       mouse stroke would come out hairline-thin. */
    const pr = (e.pointerType === 'pen' && e.pressure > 0) ? e.pressure : 0.5;
    return [(e.clientX - r.left) / r.width, (e.clientY - r.top) / r.height, +pr.toFixed(2)];
  };

  const TOOL_STYLE = {
    pen:         { alpha: 1,    scale: 1,   composite: 'source-over' },
    pencil:      { alpha: 0.78, scale: 0.9, composite: 'source-over' },
    highlighter: { alpha: 0.32, scale: 4.5, composite: 'multiply' }
  };

  function drawStroke(ctx, s, w, h) {
    const st = TOOL_STYLE[s.tool] || TOOL_STYLE.pen;
    const pts = s.points || [];
    if (pts.length < 1) return;
    const base = Math.max(0.6, s.width * st.scale * (w / 820));
    ctx.save();
    ctx.globalAlpha = st.alpha;
    ctx.globalCompositeOperation = st.composite;
    ctx.strokeStyle = s.color;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    /* A highlighter is a flat felt tip — leaning on it does not make the mark
       wider, so it keeps one width. Pen and pencil taper with pressure, which
       is what makes handwriting look written rather than plotted. */
    const varies = s.tool !== 'highlighter' && pts.some(p => p.length > 2);
    if (!varies) {
      ctx.lineWidth = base;
      ctx.beginPath();
      ctx.moveTo(pts[0][0] * w, pts[0][1] * h);
      for (let i = 1; i < pts.length; i++) {
        const p = pts[i], q = pts[i - 1];
        ctx.quadraticCurveTo(q[0] * w, q[1] * h, (q[0] + p[0]) / 2 * w, (q[1] + p[1]) / 2 * h);
      }
      ctx.stroke();
      ctx.restore();
      return;
    }

    /* Quadratics through the sample points, control points at the midpoints
       of their neighbours. Straight segments between samples read as a polygon
       once the pointer is moving quickly, which is what "it draws in lines"
       was. Width still varies per span, so this cannot be one long path. */
    const mid = (a, b) => [(a[0] + b[0]) / 2 * w, (a[1] + b[1]) / 2 * h];
    for (let i = 1; i < pts.length; i++) {
      const p0 = pts[i - 1], p1 = pts[i];
      const a = (i === 1) ? [p0[0] * w, p0[1] * h] : mid(pts[i - 2], p0);
      const b = mid(p0, p1);
      const pr = ((p0[2] == null ? 0.5 : p0[2]) + (p1[2] == null ? 0.5 : p1[2])) / 2;
      ctx.lineWidth = base * (0.45 + pr * 1.25);
      ctx.beginPath();
      ctx.moveTo(a[0], a[1]);
      ctx.quadraticCurveTo(p0[0] * w, p0[1] * h, b[0], b[1]);
      ctx.stroke();
    }
    const n2 = pts.length;
    if (n2 >= 2) {
      const b = mid(pts[n2 - 2], pts[n2 - 1]);
      ctx.beginPath();
      ctx.moveTo(b[0], b[1]);
      ctx.lineTo(pts[n2 - 1][0] * w, pts[n2 - 1][1] * h);
      ctx.stroke();
    }
    ctx.restore();
  }

  function redraw(pageEl) {
    const canvas = pageEl.querySelector('canvas');
    const page = nb.pages[+pageEl.dataset.i];
    if (!canvas || !page) return;
    const r = canvas.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (canvas.width !== Math.round(r.width * dpr)) {
      canvas.width = Math.round(r.width * dpr);
      canvas.height = Math.round(r.height * dpr);
    }
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, r.width, r.height);
    (page.strokes || []).forEach(s => drawStroke(ctx, s, r.width, r.height));
  }

  /* The preview in the create dialog is a thumbnail, where faint rules read
     fine. A full page is five times the size, and at that scale the same
     values were invisible — which is why the paper looked blank. Real
     stationery is subtle but legible; these numbers are tuned for the page,
     not the swatch. */
  function paperCss(note) {
    const dark = note.paperColor === 'black';
    const hex = note.paperColor === 'cream' ? '#faf6e8' : dark ? '#1a1a1a' : '#ffffff';
    const line = dark ? 'rgba(255,255,255,.22)' : 'rgba(15,23,42,.17)';
    const pattern = note.paperPattern || 'blank';
    let bg = '';
    if (pattern === 'dots') {
      bg = `background-image:radial-gradient(${line} 1.4px, transparent 1.5px); background-size:26px 26px;`;
    } else if (pattern === 'square') {
      bg = `background-image:linear-gradient(${line} 1px, transparent 1px),`
         + `linear-gradient(90deg, ${line} 1px, transparent 1px); background-size:26px 26px;`;
    }
    return `background-color:${hex}; ${bg}`;
  }

  function renderPages() {
    const host = $('nb-pages');
    // Only the current page is built. Snapping a long scroller "mostly" landed
    // on a page and could always be nudged off it; rendering one page cannot.
    host.innerHTML = [nb.pages[activePageIdx]].filter(Boolean).map((p) => {
      const i = activePageIdx;
      const cover = p.isCover;
      const style = cover
        ? `background:${nb.note.coverColor || '#0ea5e9'}`
        : paperCss(nb.note);
      return `<div class="nb-pageslot">
        <div class="nb-page${cover ? ' is-cover' : ''}" data-i="${i}" style="${style}">
          <canvas></canvas>
          <div class="nb-erase-cursor"></div>
          <div class="nb-pagenum">${cover ? 'Cover' : 'Page ' + (nb.pages.filter((x, j) => !x.isCover && j <= i).length)}</div>
        </div>
      </div>`;
    }).join('');
    host.querySelectorAll('.nb-page').forEach(pageEl => {
      wirePage(pageEl);
      redraw(pageEl);
      renderTexts(pageEl);
    });
    renderPageNav();
  }

  function renderPageNav() {
    const lbl = $('nb-pagelabel');
    if (lbl) lbl.textContent = (activePageIdx + 1) + ' / ' + nb.pages.length;
    const prev = $('nb-pgprev'), next = $('nb-pgnext');
    if (prev) prev.disabled = activePageIdx <= 0;
    if (next) next.disabled = activePageIdx >= nb.pages.length - 1;
  }

  function goPage(i) {
    if (!nb) return;
    const to = Math.max(0, Math.min(nb.pages.length - 1, i));
    if (to === activePageIdx) return;
    if (nb.dirty.size) savePages();
    activePageIdx = to;
    closeTextBar();
    renderPages();
  }



  /* Rough luminance test — a dark cover needs pale ink, a pale one needs dark.
     Getting this wrong is how you end up typing white on white and thinking
     the tool is broken. */
  function isDark(hex) {
    const h = String(hex || '').replace('#', '');
    if (h.length !== 6) return false;
    const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) < 150;
  }

  function renderTexts(pageEl) {
    const page = nb.pages[+pageEl.dataset.i];
    const p = page;
    pageEl.querySelectorAll('.nb-txtbox').forEach(el => el.remove());
    (page.texts || []).forEach((t, ti) => {
      /* Wrapper holds the chrome; only the inner div is editable. Anything
         inside a contenteditable is CONTENT as far as the browser is concerned,
         handles included — the caret positions itself around them and the text
         wraps at them. Keeping them as siblings is the whole fix. */
      const box = document.createElement('div');
      box.className = 'nb-txtbox';
      box.dataset.ti = ti;

      const el = document.createElement('div');
      el.className = 'nb-txt';
      el.contentEditable = 'true';
      el.dataset.ti = ti;
      box.style.left = (t.x * 100) + '%';
      box.style.top = (t.y * 100) + '%';
      /* Width is a fraction of the page, so it survives a change of screen the
         same way strokes do. Default is whatever room is left, capped at half
         a page — starting a box that already runs to the margin looks broken. */
      const w = t.w || Math.min(0.5, Math.max(0.18, 1 - t.x - 0.04));
      t.w = w;
      box.style.width = (w * 100) + '%';
      el.style.fontSize = (t.size || 24) + 'px';
      el.style.fontWeight = t.bold ? '700' : '400';
      el.style.fontStyle = t.italic ? 'italic' : 'normal';
      const defaultInk = (nb.note.paperColor === 'black' || (p.isCover && isDark(nb.note.coverColor)))
        ? '#f8fafc' : '#111827';
      el.style.color = t.color || defaultInk;
      el.textContent = t.text || '';
      el.addEventListener('focus', () => openTextBar(pageEl, el));
      el.addEventListener('input', () => {
        // Nothing but text lives in here now, so this can be straightforward.
        t.text = el.innerText;
        markDirty(+pageEl.dataset.i);
        if (editingText && editingText.el === el) openTextBar(pageEl, el);
      });
      el.addEventListener('blur', () => setTimeout(() => {
        const ae = document.activeElement;
        // Focus landing on the format bar is not leaving the text — the size
        // dropdown steals focus the moment it opens, which is why the bar used
        // to disappear the instant you reached for it.
        if (ae === el) return;
        if (ae && ae.closest && ae.closest('#nb-textbar')) return;
        closeTextBar(el);
      }, 150));

      /* A grab handle rather than dragging the box itself — the box is a text
         field, and making its body draggable would fight every attempt to
         select a word. */
      const rz = document.createElement('span');
      rz.className = 'nb-txt-rz';
      rz.title = 'Drag to resize';
      let rzDrag = null;
      rz.addEventListener('pointerdown', ev => {
        ev.preventDefault(); ev.stopPropagation();
        rz.setPointerCapture(ev.pointerId);
        rzDrag = pageEl.getBoundingClientRect();
      });
      rz.addEventListener('pointermove', ev => {
        if (!rzDrag) return;
        const nw = (ev.clientX - (rzDrag.left + t.x * rzDrag.width)) / rzDrag.width;
        t.w = Math.min(1 - t.x - 0.01, Math.max(0.08, nw));
        box.style.width = (t.w * 100) + '%';
        if (editingText && editingText.el === el) openTextBar(pageEl, el);
      });
      const endRz = () => { if (rzDrag) { rzDrag = null; markDirty(+pageEl.dataset.i); } };
      rz.addEventListener('pointerup', endRz);
      rz.addEventListener('pointercancel', endRz);
      box.appendChild(rz);

      const grip = document.createElement('span');
      grip.className = 'nb-txt-grip';
      grip.title = 'Drag to move';
      grip.innerHTML = '&#10021;';
      let drag = null;
      grip.addEventListener('pointerdown', ev => {
        ev.preventDefault();
        ev.stopPropagation();
        grip.setPointerCapture(ev.pointerId);
        const pr = pageEl.getBoundingClientRect();
        drag = { pr, dx: ev.clientX - (pr.left + t.x * pr.width), dy: ev.clientY - (pr.top + t.y * pr.height) };
      });
      grip.addEventListener('pointermove', ev => {
        if (!drag) return;
        // Clamped so a box can never be dragged off the page and lost.
        const nx = Math.min(0.98, Math.max(0, (ev.clientX - drag.dx - drag.pr.left) / drag.pr.width));
        const ny = Math.min(0.98, Math.max(0, (ev.clientY - drag.dy - drag.pr.top) / drag.pr.height));
        t.x = nx; t.y = ny;
        box.style.left = (nx * 100) + '%';
        box.style.top = (ny * 100) + '%';
        if (editingText && editingText.el === el) openTextBar(pageEl, el);
      });
      const endDrag = () => { if (drag) { drag = null; markDirty(+pageEl.dataset.i); } };
      grip.addEventListener('pointerup', endDrag);
      grip.addEventListener('pointercancel', endDrag);
      box.appendChild(el);
      box.appendChild(grip);
      pageEl.appendChild(box);
    });
  }

  function openTextBar(pageEl, el) {
    editingText = { pageEl, el };
    const bar = $('nb-textbar');
    const r = el.getBoundingClientRect();
    bar.hidden = false;
    bar.style.left = Math.max(10, r.left) + 'px';
    bar.style.top = Math.max(10, r.top - 48) + 'px';
    const t = nb.pages[+pageEl.dataset.i].texts[+el.dataset.ti];
    $('nb-tb-size').value = String(t.size || 24);
  }
  function closeTextBar(el) {
    const bar = $('nb-textbar');
    if (editingText && (!el || editingText.el === el)) { bar.hidden = true; editingText = null; }
  }

  function markDirty(i) {
    nb.dirty.add(i);
    $('nb-saved').textContent = 'Saving…';
    if (saveTimer) clearTimeout(saveTimer);
    saveTimer = setTimeout(savePages, 900);
  }

  /* Stroke eraser, not pixel eraser: dragging over a stroke removes the whole
     stroke. It is what people expect from "rub it out" on a tablet, and it is
     the only kind that works when strokes are vectors. */
  /* Two erasers, because they answer different questions.

     STROKE: touch a line anywhere and the whole line goes. Right when you drew
     the wrong thing.

     STANDARD: rubs out only the part you passed over. Strokes are vectors, so
     there are no pixels to clear — instead the stroke is SPLIT, dropping the
     points inside the eraser and keeping the runs either side as separate
     strokes. That is what makes it feel like rubbing rather than deleting, and
     it is why a stroke can end up with a hole in the middle. */
  /* Distance from a point to a line SEGMENT — see the whiteboard for why.
     Testing only the sample points let a small eraser slip between two of them
     on a fast stroke and remove nothing. */
  function segDist(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const l2 = dx * dx + dy * dy;
    if (!l2) return Math.hypot(px - ax, py - ay);
    let t = ((px - ax) * dx + (py - ay) * dy) / l2;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
  }

  function eraseAt(page, x, y, pageEl) {
    const strokes = page.strokes || [];
    if (!strokes.length) return;
    const r = eraserR;
    const hitAt = (pts, i) => {
      const p = pts[i];
      if (Math.hypot(p[0] - x, p[1] - y) <= r) return true;
      if (i > 0 && segDist(x, y, pts[i - 1][0], pts[i - 1][1], p[0], p[1]) <= r) return true;
      if (i < pts.length - 1 && segDist(x, y, p[0], p[1], pts[i + 1][0], pts[i + 1][1]) <= r) return true;
      return false;
    };

    if (eraserMode === 'stroke') {
      const before = strokes.length;
      page.strokes = strokes.filter(s => !(s.points || []).some((p, i) => hitAt(s.points, i)));
      if (page.strokes.length !== before) { redraw(pageEl); markDirty(+pageEl.dataset.i); }
      return;
    }

    let changed = false;
    const out = [];
    strokes.forEach(s => {
      const pts = s.points || [];
      if (!pts.some((p, i) => hitAt(pts, i))) { out.push(s); return; }
      changed = true;
      let run = [];
      pts.forEach((p, i) => {
        if (hitAt(pts, i)) {
          // A single leftover point draws nothing, so only keep real runs.
          if (run.length > 1) out.push({ ...s, points: run });
          run = [];
        } else run.push(p);
      });
      if (run.length > 1) out.push({ ...s, points: run });
    });
    if (changed) { page.strokes = out; redraw(pageEl); markDirty(+pageEl.dataset.i); }
  }

  function wirePage(pageEl) {
    const canvas = pageEl.querySelector('canvas');
    const i = +pageEl.dataset.i;
    let drawing = null;

    canvas.addEventListener('pointerdown', e => {
      activePageIdx = i;
      const page = nb.pages[i];
      const [x, y, pr] = rel(e, canvas);

      if (tool === 'text') {
        page.texts = page.texts || [];
        page.texts.push({ x, y, text: '', size: 24, bold: false, italic: false });
        markDirty(i);
        renderTexts(pageEl);
        const el = pageEl.querySelector(`.nb-txtbox[data-ti="${page.texts.length - 1}"] .nb-txt`);
        if (el) { el.focus(); openTextBar(pageEl, el); }
        return;
      }
      if (tool === 'eraser') { canvas.setPointerCapture(e.pointerId); drawing = { erase: true }; eraseAt(page, x, y, pageEl); return; }

      canvas.setPointerCapture(e.pointerId);
      nbRedo.delete(i);   // a new stroke abandons anything undo had set aside
      drawing = { tool: toolType, color, width, points: [[x, y, pr]] };
      page.strokes = page.strokes || [];
      page.strokes.push(drawing);
    });

    const cursor = pageEl.querySelector('.nb-erase-cursor');
    const moveCursor = e => {
      if (!cursor) return;
      if (tool !== 'eraser') { cursor.style.display = 'none'; return; }
      const r = canvas.getBoundingClientRect();
      const d = eraserR * 2 * r.width;
      cursor.style.display = 'block';
      cursor.style.width = d + 'px';
      cursor.style.height = d + 'px';
      cursor.style.left = (e.clientX - r.left - d / 2) + 'px';
      cursor.style.top = (e.clientY - r.top - d / 2) + 'px';
    };
    canvas.addEventListener('pointerleave', () => { if (cursor) cursor.style.display = 'none'; });

    canvas.addEventListener('pointermove', e => {
      moveCursor(e);
      if (!drawing) return;
      const [x, y, pr] = rel(e, canvas);
      if (drawing.erase) { eraseAt(nb.pages[i], x, y, pageEl); return; }
      drawing.points.push([x, y, pr]);
      redraw(pageEl);
    });

    const end = () => {
      if (!drawing) return;
      if (!drawing.erase) {
        // A tap with no movement leaves a one-point stroke that draws nothing.
        if (drawing.points.length < 2) nb.pages[i].strokes.pop();
        markDirty(i);
      }
      drawing = null;
    };
    canvas.addEventListener('pointerup', end);
    canvas.addEventListener('pointercancel', end);
    canvas.addEventListener('pointerleave', end);
  }

  /* ---- Persistence ------------------------------------------------------
     One document per page. Firestore caps a document at 1 MB and a used
     notebook would pass that as a single blob, so the cap lands per page —
     and opening page one no longer means loading page forty. */
  function pagesCol() {
    return fbDb.collection('teams').doc(activeTeamId)
      .collection('notes').doc(nb.note.id).collection('pages');
  }

  async function savePages() {
    if (!nb || !nb.dirty.size) return;
    const ids = [...nb.dirty];
    nb.dirty.clear();
    try {
      await Promise.all(ids.map(i => {
        const p = nb.pages[i];
        if (!p) return null;
        return pagesCol().doc(p.id).set({
          index: i,
          isCover: !!p.isCover,
          strokesJson: JSON.stringify(p.strokes || []),
          textsJson: JSON.stringify(p.texts || []),
          updatedAt: new Date().toISOString()
        }, { merge: true });
      }));
      $('nb-saved').textContent = 'Saved';
    } catch (e) {
      console.warn('notebook save failed:', e && (e.code || e.message));
      $('nb-saved').textContent = 'Not saved';
      ids.forEach(i => nb.dirty.add(i));
    }
  }

  window.nbOpen = async function nbOpen(note) {
    document.body.classList.add('nb-full');
    if (nb && nb.note.id === note.id) return;
    nb = { note, pages: [], dirty: new Set() };
    $('nb-title').value = note.title || '';
    $('nb-saved').textContent = '';
    $('nb-pages').innerHTML = '<div style="color:var(--muted); font-size:13px; padding:40px">Opening…</div>';
    try {
      const snap = await pagesCol().orderBy('index').get();
      nb.pages = snap.docs.map(d => {
        const v = d.data();
        let strokes = [], texts = [];
        try { strokes = JSON.parse(v.strokesJson || '[]'); } catch (e) {}
        try { texts = JSON.parse(v.textsJson || '[]'); } catch (e) {}
        return { id: d.id, isCover: !!v.isCover, strokes, texts };
      });
    } catch (e) {
      console.warn('notebook load failed:', e && (e.code || e.message));
    }
    activePageIdx = 0;   // always open at the front
    if (!nb.pages.length) {
      // A brand-new notebook: a cover if it was given one, then a first page.
      if (note.hasCover) nb.pages.push(newPage(true));
      nb.pages.push(newPage(false));
      nb.pages.forEach((p, i) => nb.dirty.add(i));
      savePages();
    }
    renderPages();
  };

  window.nbClose = function nbClose() {
    document.body.classList.remove('nb-full');
    if (saveTimer) { clearTimeout(saveTimer); saveTimer = null; }
    if (nb && nb.dirty.size) savePages();
    nb = null;
    closeTextBar();
  };

  function newPage(isCover) {
    return { id: 'pg' + Date.now().toString(36) + Math.random().toString(36).slice(2, 6),
             isCover: !!isCover, strokes: [], texts: [] };
  }

  function addPage(where) {
    if (!nb) return;
    const p = newPage(false);
    let at = nb.pages.length;
    if (where === 'before') at = Math.max(nb.note.hasCover ? 1 : 0, activePageIdx);
    else if (where === 'after') at = activePageIdx + 1;
    nb.pages.splice(at, 0, p);
    // Indexes shifted, so every page from here on has to be rewritten.
    for (let i = at; i < nb.pages.length; i++) nb.dirty.add(i);
    activePageIdx = at;   // land on the page you just made
    renderPages();
    markDirty(at);
  }

  // ---- toolbar wiring ----
  const setTool = t => {
    tool = t;
    document.querySelectorAll('.nb-tool').forEach(b => b.classList.toggle('active', b.dataset.tool === t));
  };
  document.querySelectorAll('.nb-tool').forEach(b =>
    b.addEventListener('click', () => {
      setTool(b.dataset.tool);
      if (b.dataset.tool === 'pen') togglePop('nb-pen-pop');
      if (b.dataset.tool === 'eraser') togglePop('nb-eraser-pop');
    }));

  document.querySelectorAll('#nb-erasetype button').forEach(b =>
    b.addEventListener('click', () => {
      eraserMode = b.dataset.e;
      document.querySelectorAll('#nb-erasetype button').forEach(x => x.classList.toggle('on', x === b));
      const hint = $('nb-erase-hint');
      if (hint) hint.textContent = eraserMode === 'stroke'
        ? 'Touch a line and the whole line goes.'
        : 'Rubs out only what you pass over.';
    }));
  document.querySelectorAll('#nb-erasesize button').forEach(b =>
    b.addEventListener('click', () => {
      eraserR = parseFloat(b.dataset.r);
      document.querySelectorAll('#nb-erasesize button').forEach(x => x.classList.toggle('on', x === b));
    }));

  function togglePop(id) {
    ['nb-pen-pop', 'nb-color-pop', 'nb-addpage-pop', 'nb-eraser-pop'].forEach(x => {
      const el = $(x);
      if (el) el.hidden = (x !== id) ? true : !el.hidden;
    });
  }
  document.addEventListener('click', e => {
    if (e.target.closest && (e.target.closest('.nb-pop') || e.target.closest('.nb-pop-wrap'))) return;
    ['nb-pen-pop', 'nb-color-pop', 'nb-addpage-pop', 'nb-eraser-pop'].forEach(x => { const el = $(x); if (el) el.hidden = true; });
  });

  document.querySelectorAll('#nb-tooltype button').forEach(b =>
    b.addEventListener('click', () => {
      toolType = b.dataset.t;
      setTool('pen');
      document.querySelectorAll('#nb-tooltype button').forEach(x => x.classList.toggle('on', x === b));
    }));
  document.querySelectorAll('#nb-width button').forEach(b =>
    b.addEventListener('click', () => {
      width = parseFloat(b.dataset.w);
      document.querySelectorAll('#nb-width button').forEach(x => x.classList.toggle('on', x === b));
    }));

  function pickColor(hex) {
    color = hex;
    setTool('pen');
    document.querySelectorAll('.nb-swatch[data-color]').forEach(x =>
      x.classList.toggle('on', x.dataset.color === hex));
  }
  document.querySelectorAll('.nb-swatch[data-color]').forEach(b =>
    b.addEventListener('click', () => pickColor(b.dataset.color)));

  const colorsHost = $('nb-colors');
  if (colorsHost) {
    colorsHost.innerHTML = NB_PALETTE.map(c =>
      `<button type="button" class="nb-swatch" data-pal="${c}" style="background:${c}"></button>`).join('');
    colorsHost.querySelectorAll('[data-pal]').forEach(b =>
      b.addEventListener('click', () => { pickColor(b.dataset.pal); togglePop(null); }));
  }
  const colorBtn = $('nb-color-btn');
  if (colorBtn) colorBtn.addEventListener('click', e => { e.stopPropagation(); togglePop('nb-color-pop'); });

  const addBtn = $('nb-addpage-btn');
  if (addBtn) addBtn.addEventListener('click', e => { e.stopPropagation(); togglePop('nb-addpage-pop'); });
  document.querySelectorAll('#nb-addpage-pop [data-add]').forEach(b =>
    b.addEventListener('click', () => { addPage(b.dataset.add); togglePop(null); }));

  function repaintActive() {
    const el = $('nb-pages').querySelector(`.nb-page[data-i="${activePageIdx}"]`);
    if (el) redraw(el);
    markDirty(activePageIdx);
  }
  const undoBtn = $('nb-undo');
  if (undoBtn) undoBtn.addEventListener('click', () => {
    if (!nb) return;
    const page = nb.pages[activePageIdx];
    if (!page || !(page.strokes || []).length) return;
    const stack = nbRedo.get(activePageIdx) || [];
    stack.push(page.strokes.pop());
    nbRedo.set(activePageIdx, stack);
    repaintActive();
  });
  const redoBtn = $('nb-redo');
  if (redoBtn) redoBtn.addEventListener('click', () => {
    if (!nb) return;
    const stack = nbRedo.get(activePageIdx) || [];
    if (!stack.length) return;
    (nb.pages[activePageIdx].strokes = nb.pages[activePageIdx].strokes || []).push(stack.pop());
    repaintActive();
  });

  function nbLeave() {
    nbClose();
    if (typeof notesCloseEditor === 'function') notesCloseEditor();
  }
  const prevBtn = $('nb-pgprev');
  if (prevBtn) prevBtn.addEventListener('click', () => goPage(activePageIdx - 1));
  const nextBtn = $('nb-pgnext');
  if (nextBtn) nextBtn.addEventListener('click', () => goPage(activePageIdx + 1));

  // Arrows page through — the first thing anyone tries once scrolling is gone.
  // Not while typing, where arrows move the caret.
  document.addEventListener('keydown', e => {
    if (!nb) return;
    const ae = document.activeElement;
    if (ae && (ae.classList && ae.classList.contains('nb-txt') || ae.tagName === 'INPUT' || ae.tagName === 'SELECT')) return;
    if (e.key === 'ArrowRight' || e.key === 'PageDown') { e.preventDefault(); goPage(activePageIdx + 1); }
    if (e.key === 'ArrowLeft'  || e.key === 'PageUp')   { e.preventDefault(); goPage(activePageIdx - 1); }
  });

  const backBtn = $('nb-back');
  if (backBtn) backBtn.addEventListener('click', nbLeave);

  /* Escape leaves too — every full-screen mode trains people to try it. Not
     while typing in a text box, though: there Escape means "stop editing this",
     and throwing someone out of the notebook mid-sentence would be brutal. */
  document.addEventListener('keydown', e => {
    if (e.key !== 'Escape' || !nb) return;
    if (document.activeElement && document.activeElement.classList &&
        document.activeElement.classList.contains('nb-txt')) {
      document.activeElement.blur();
      return;
    }
    if (!document.querySelectorAll('.nb-pop:not([hidden])').length) nbLeave();
  });

  const titleEl = $('nb-title');
  if (titleEl) titleEl.addEventListener('input', () => {
    if (typeof notesPatchActive === 'function') notesPatchActive({ title: titleEl.value });
  });

  // ---- text formatting bar ----
  const withText = fn => {
    if (!editingText) return;
    const t = nb.pages[+editingText.pageEl.dataset.i].texts[+editingText.el.dataset.ti];
    fn(t, editingText.el);
    markDirty(+editingText.pageEl.dataset.i);
  };
  const TB_INK = ['#111827', '#ffffff', '#dc2626', '#2563eb', '#16a34a', '#ca8a04'];
  const inkHost = $('nb-tb-colors');
  if (inkHost) {
    inkHost.innerHTML = TB_INK.map(c =>
      `<button type="button" data-ink="${c}" style="background:${c}" title="Text colour"></button>`).join('');
    inkHost.querySelectorAll('[data-ink]').forEach(b =>
      b.addEventListener('mousedown', e => {
        e.preventDefault();
        withText((t, el) => { t.color = b.dataset.ink; el.style.color = t.color; });
        inkHost.querySelectorAll('[data-ink]').forEach(x => x.classList.toggle('on', x === b));
      }));
  }

  const sizeSel = $('nb-tb-size');
  if (sizeSel) sizeSel.addEventListener('change', () => {
    const target = editingText && editingText.el;
    withText((t, el) => { t.size = +sizeSel.value; el.style.fontSize = t.size + 'px'; });
    // Put the caret back where it was, rather than leaving it in the dropdown.
    if (target) target.focus();
  });
  const boldBtn = $('nb-tb-bold');
  if (boldBtn) boldBtn.addEventListener('mousedown', e => { e.preventDefault();
    withText((t, el) => { t.bold = !t.bold; el.style.fontWeight = t.bold ? '700' : '400'; }); });
  const itBtn = $('nb-tb-italic');
  if (itBtn) itBtn.addEventListener('mousedown', e => { e.preventDefault();
    withText((t, el) => { t.italic = !t.italic; el.style.fontStyle = t.italic ? 'italic' : 'normal'; }); });
  const delBtn = $('nb-tb-del');
  if (delBtn) delBtn.addEventListener('mousedown', e => {
    e.preventDefault();
    if (!editingText) return;
    const pageEl = editingText.pageEl;
    const page = nb.pages[+pageEl.dataset.i];
    page.texts.splice(+editingText.el.dataset.ti, 1);
    closeTextBar();
    renderTexts(pageEl);
    markDirty(+pageEl.dataset.i);
  });

  // Pages are sized in CSS, so a resize changes the pixel grid under them.
  let rzTimer = null;
  window.addEventListener('resize', () => {
    if (!nb) return;
    clearTimeout(rzTimer);
    rzTimer = setTimeout(() => {
      $('nb-pages').querySelectorAll('.nb-page').forEach(redraw);
    }, 150);
  });

  // Leaving with unsaved strokes is the one loss that would matter.
  window.addEventListener('beforeunload', () => { if (nb && nb.dirty.size) savePages(); });
}

onReady(initNotebook);
